import {injectable, inject} from 'inversify';
import {DeferredPromise} from 'openfin-service-async';
import {ApplicationInfo} from 'openfin/_v2/api/system/application';
import {ApplicationOption} from 'openfin/_v2/api/application/applicationOption';

import {Inject} from '../common/Injectables';
import {ServiceStore} from '../store/ServiceStore';
import {RootState} from '../store/State';
import {Action} from '../store/Store';
import {PostLogin} from '../store/partials/Authentication';
import {CreateWorkspace, PostWorkspacesFetch, RestoreWorkspace, DeleteWorkspace, UpdateWorkspaceStatus, UpdateWorkspace} from '../store/partials/Workspaces';
import {UserLayout, NewUserLayout, WorkspaceApplication, WorkspaceStatusStates} from '../types/commons';
import {API} from '../model/API';
import {UserData} from '../model/api/User';
import {uuidv4} from '../utils/createUuid';
import {ErrorHandler} from '../model/ErrorHandler';
import {Snapshot} from '../types/interfaces';

export interface UserWorkspaceMeta {
    id: string;
    name: string;
    tags: string[];
    description: string;
}

export type UpdatableWorkspaceMeta = Partial<Omit<UserWorkspaceMeta, 'id'>>;

interface WorkspaceMap {[id: string]: Snapshot}
interface RemoteIdMap {[id: string]: DeferredPromise<string>}

const filteredUuids: string[] = [
    'notifications-service',
    'layouts-service',
    'fdc3-service',
    'osLaunchpadMain'
];

@injectable()
export class Workspaces {
    private _workspaces: WorkspaceMap;
    private _remoteIds: RemoteIdMap;

    public constructor(
        @inject(Inject.API) private readonly api: API,
        @inject(Inject.STORE) private readonly store: ServiceStore
    ) {
        this.store.onAction.add(this.onAction, this);
        this._workspaces = {};
        this._remoteIds = {};
    }

    private async onAction(action: Action<RootState>): Promise<void> {
        if (action instanceof PostLogin) {
            const meta = await this.fetchWorkspaces();
            await this.store.dispatch(new PostWorkspacesFetch(meta));
        } else if (action instanceof CreateWorkspace) {
            await this.createWorkspace(action);
        } else if (action instanceof RestoreWorkspace) {
            const {id} = action;
            const workspace: Snapshot = this._workspaces[id];
            const status = await this.restoreSnapshot(workspace.apps);
            await this.store.dispatch(new UpdateWorkspaceStatus(id, status));
        } else if (action instanceof DeleteWorkspace) {
            await this.deleteWorkspace(action);
        } else if (action instanceof UpdateWorkspace) {
            await this.updateWorkspace(action);
        }
    }

    private async fetchWorkspaces(): Promise<UserWorkspaceMeta[]> {
        const workspaces: UserLayout[] = await this.api.get(UserData.SNAPSHOTS);
        const workspaceMap: WorkspaceMap = {};
        const remoteIdMap: RemoteIdMap = {};

        const meta = workspaces.map((workspace: UserLayout) => {
            const {id: remoteId, name} = workspace;

            const localId = uuidv4();

            // Generate fake tags/description, until these are added to API
            const tags = ['Clients', 'Buy', 'Equity', 'Trading', 'Research', 'Market'].filter(() => Math.random() > 0.5);
            const description = 'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Donec non massa odio. \
Maecenas non mi non mi volutpat porta vel at elit. Suspendisse mattis ut massa in molestie. Quisque quis bibendum \
nunc, et ornare tellus. Morbi non neque id mi fringilla varius non non urna. Ut porttitor maximus vehicula.';

            // Populate workspace look-up
            if (workspace.snapshot) {
                workspaceMap[localId] = workspace.snapshot;
            }

            const deferredPromise = new DeferredPromise<string>();
            deferredPromise.resolve(remoteId);
            remoteIdMap[localId] = deferredPromise;

            return {id: localId, name, tags, description};
        });
        this._workspaces = workspaceMap;
        this._remoteIds = remoteIdMap;

        return meta;
    }

    private async generateSnapshot(): Promise<Snapshot> {
        const finApps: ApplicationInfo[] = await fin.System.getAllApplications();
        const filteredApps: ApplicationInfo[] = finApps.filter((app) => {
            return !filteredUuids.includes(app.uuid) && app.isRunning;
        });
        return {
            apps: await Promise.all(filteredApps.map(async (app) => this.getApplication(app.uuid)))
        };
    }

    private async restoreSnapshot(snapshot: WorkspaceApplication[]): Promise<WorkspaceStatusStates> {
        let result: WorkspaceStatusStates = WorkspaceStatusStates.None;

        await Promise.all(snapshot.map(async (application) => {
            try {
                if (application.type === 'manifest') {
                    await fin.Application.startFromManifest(application.manifestUrl);
                } else if (application.type === 'programmatic') {
                    await fin.Application.start(application.initialOptions);
                } // TODO: handle native apps (Service-931)
            } catch (error) {
                ErrorHandler.for(error).log();
                result = WorkspaceStatusStates.Error;
            }
        }));

        return result;
    }

    public async getApplication(uuid: string): Promise<WorkspaceApplication> {
        const app = fin.Application.wrapSync({uuid});
        const info = await app.getInfo();
        const isProgrammatic: boolean = !!info.parentUuid;

        if (isProgrammatic) {
            const initialOptions: ApplicationOption = info.initialOptions as ApplicationOption;
            return {
                type: 'programmatic',
                initialOptions
            };
        } else {
            return {
                type: 'manifest',
                manifestUrl: info.manifestUrl
            };
        }
    }

    private async updateWorkspace(action: UpdateWorkspace): Promise<void> {
        const {id: localId, shouldUpdateSnapshot} = action;
        const meta = this.store.state.workspaces.workspaces[localId];

        let snapshot: Snapshot = this._workspaces[localId];

        if (shouldUpdateSnapshot) {
            snapshot = await this.generateSnapshot();
            this._workspaces[localId] = snapshot;
        }

        // The server does not accept anything else besides name, snapshot, so tags + description are not updatable as this time. MAYBE TODO
        const layout: NewUserLayout = {name: meta.name, snapshot};

        const remoteId = await this._remoteIds[localId].promise;
        await this.api.put(UserData.SNAPSHOTS, {urlParams: {id: remoteId}, body: layout});
    }

    private async createWorkspace(action: CreateWorkspace): Promise<void> {
        const {id: localId, name, tags, description} = action;
        const meta: UserWorkspaceMeta = {id: localId, name, tags, description};

        const workspace: Snapshot = await this.generateSnapshot();

        this._workspaces[localId] = workspace;

        const layout: NewUserLayout = {name: meta.name, snapshot: workspace};

        this._remoteIds[localId] = new DeferredPromise();
        const result = await this.api.post(UserData.SNAPSHOTS, {body: layout});

        this._remoteIds[localId].resolve(result.snapshot.id);
    }

    private async deleteWorkspace(action: DeleteWorkspace): Promise<void> {
        const localId = action.id;
        const remoteId = await this._remoteIds[localId].promise;

        delete this._workspaces[localId];
        delete this._remoteIds[localId];

        await this.api.delete(UserData.SNAPSHOTS, {urlParams: {id: remoteId}});
    }
}
